import styles from './style.scss';

import {html} from 'lit';
import {
  customElement,
  property,
  queryAssignedElements,
} from 'lit/decorators.js';

import {FlywheelComponent} from '#lit/lib/component.js';
import {
  ImmutableComponent,
  modelGetter,
  modelSetter,
} from '#lit/components/mixins/immutable_component/index.js';
import type {Constructor} from '#lit/types/classes.js';
import type {Nullable} from '#lit/types/general.js';
import {hasProperty} from '#lit/utils/objects.js';
import {isBool} from '#lit/utils/index.js';
import {isHTMLElement} from '#lit/utils/dom.js';
import {COMPONENT_TAG as ITEM_TAG} from '#lit/components/base/floating_surface_list_item/index.js';
import type {Model, ListItemSelection} from './architecture.js';
import {Msg, main} from './architecture.js';

export const COMPONENT_TAG = 'fly-surface-list';

export type ActiveEvent = {
  detail: {active: boolean};
};

export type SelectEvent = {
  detail: {
    current: Nullable<ListItemSelection>;
    previous: Nullable<ListItemSelection>;
  };
};

type EventMap = {
  active: ActiveEvent;
  selection: SelectEvent;
};

const propSetter = modelSetter<Msg>();

export function isFloatingList(value: Node): value is FlyFloatingSurfaceList {
  return isHTMLElement(value) && value.nodeName.toLowerCase() === COMPONENT_TAG;
}

@customElement(COMPONENT_TAG)
export class FlyFloatingSurfaceList extends ImmutableComponent<
  Constructor<FlywheelComponent>,
  Model,
  Msg
>({component: FlywheelComponent, main}) {
  static get styles() {
    return [styles];
  }

  @queryAssignedElements()
  listContent!: Array<HTMLElement>;

  private keyListenersUnsubscribe = () => {};

  @property({type: Boolean, reflect: true})
  get active() {
    return modelGetter(this, 'active');
  }
  set active(value: boolean) {
    const setter = propSetter(this, 'active');
    setter({value, msg: Msg.active(value)});
  }

  private emitEvent<EventName extends keyof EventMap>(
    eventName: EventName,
    detail: EventMap[EventName]['detail'],
    options?: EventInit
  ): void {
    const event = new CustomEvent(eventName, {
      detail,
      bubbles: true,
      cancelable: true,
      composed: true,
      ...options,
    });
    this.dispatchEvent(event);
  }

  private validateContent() {
    const isValid = this.listContent.every(
      element => element.nodeName.toLowerCase() === ITEM_TAG
    );

    if (!isValid) {
      console.error(`Invalid element passed on ${COMPONENT_TAG}`, this);
    }
  }

  private startKeyListeners() {
    const abortController = new AbortController();
    document.addEventListener('keydown', this.handleKeydown.bind(this), {
      signal: abortController.signal,
    });
    this.keyListenersUnsubscribe = () => abortController.abort();
  }

  private stopKeyListeners() {
    this.keyListenersUnsubscribe();
  }

  private selectItem(direction: 'previous' | 'next' | 'first' | 'last'): void {
    const lastIndex = this.listContent.length - 1;
    const currentSelection = this.model.selection;
    const indexModifier = direction === 'previous' ? -1 : 1;

    let nextIndex: number;

    switch (direction) {
      case 'first':
        nextIndex = 0;
        break;

      case 'last':
        nextIndex = lastIndex;
        break;

      default:
        nextIndex = currentSelection
          ? Math.min(
              lastIndex,
              Math.max(0, currentSelection.index + indexModifier)
            )
          : 0;
    }

    if (nextIndex === currentSelection?.index) return;

    this.handleUpdate(
      Msg.select({
        index: nextIndex,
        item: this.listContent[nextIndex],
        isFirst: nextIndex === 0,
        isLast: nextIndex === lastIndex,
      })
    );
  }

  private handleKeydown(event: KeyboardEvent) {
    switch (event.key) {
      case 'ArrowUp': {
        event.preventDefault();
        return this.selectItem('previous');
      }
      case 'ArrowDown': {
        event.preventDefault();
        return this.selectItem('next');
      }
      case 'Home': {
        event.preventDefault();
        return this.selectItem('first');
      }
      case 'End': {
        event.preventDefault();
        return this.selectItem('last');
      }
    }
  }

  onActive(active: boolean) {
    this.emitEvent('active', {active});

    if (active) {
      this.startKeyListeners();
      this.setAttribute('active', 'true');
    } else {
      this.stopKeyListeners();
      this.removeAttribute('active');
    }
  }

  onSelection(
    selection: Nullable<ListItemSelection>,
    oldValue: Nullable<ListItemSelection>
  ) {
    if (selection && hasProperty(selection?.item, 'selected', isBool)) {
      selection.item.selected = true;
    }
    if (oldValue && hasProperty(oldValue?.item, 'selected', isBool)) {
      oldValue.item.selected = false;
    }
    this.emitEvent('selection', {
      current: selection,
      previous: oldValue,
    });
  }

  render() {
    return html`<slot @slotChange=${this.validateContent}></slot> `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    [COMPONENT_TAG]: FlyFloatingSurfaceList;
  }
}
